import { useCallback, useEffect, useMemo } from 'react';

import { getServiceId } from '@/api/tokens.helper';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { useInvoice } from '@/store/expenses-store/invoices/invoices.store';
import type {
  TransactionCategory,
  TransactionRow,
} from '@/store/expenses-store/transactions/transactions.dto.ts';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import { useServiceProvidersQuery } from '@/store/service-providers/service-providers.store';
import { transformExpensesToRows } from '@pages/Expenses/TransactionsPage/utils/transformExpense.ts';

type DateRange = { from: string; to: string };

/**
 * One transaction row needs three services to answer, and each of them is gated separately:
 * the transactions and their categories on wallet's `transactionsBasic`, the invoice a row
 * links to on `Invoices`, and the vendor behind it on `BasicServiceProvider`. A plan can
 * carry any subset — a navigate vessel reads transactions but has no provider catalogue —
 * so each request asks its own question first. Whatever is withheld simply goes unresolved:
 * the row keeps its raw vendor id or its invoice-less amount rather than the page failing.
 */
export function useBudgetTransactions(dateRange: DateRange): {
  rows: TransactionRow[];
  categories: TransactionCategory[];
  loading: boolean;
  error: string | null;
  /**
   * Re-reads the transactions of the current range — for changes made outside this hook that
   * move them, such as deleting a category. The category list refreshes itself: every store
   * mutation on a category re-reads it.
   */
  refreshTransactions: () => Promise<void>;
} {
  const {
    transactions,
    loading,
    error,
    getAllTransactions,
    transactionCategories,
    getTransactionsCategories,
  } = useTransactionsStore();
  const { invoices, getInvoices } = useInvoice();

  const { can, canAny } = usePermissions();
  const canReadTransactions = canAny([
    PERMISSIONS.wallet.TransactionsBasic,
    PERMISSIONS.wallet.Transactions,
  ]);
  const canReadInvoices = can(PERMISSIONS.wallet.Invoices);
  // Mirrors service-provider's `basicServiceProvider` middleware: navigate carries the
  // internal right without the basic one, and the catalogue opens on either.
  const canReadProviders = canAny([
    PERMISSIONS['service-provider'].BasicServiceProvider,
    PERMISSIONS['service-provider'].ServiceProviderInternal,
  ]);

  const vesselId = getServiceId();
  const spQuery = useServiceProvidersQuery(
    100,
    { vesselId, search: '' },
    { enabled: canReadProviders },
  );

  useEffect(() => {
    if (canReadTransactions) void getTransactionsCategories();
    if (canReadInvoices) void getInvoices();
  }, [getTransactionsCategories, getInvoices, canReadTransactions, canReadInvoices]);

  useEffect(() => {
    if (!canReadTransactions) return;
    void getAllTransactions({ from: dateRange.from, to: dateRange.to });
  }, [getAllTransactions, dateRange.from, dateRange.to, canReadTransactions]);

  const serviceProvidersMap = useMemo(() => {
    const serviceProviders = spQuery.data?.pages?.flatMap((p) => p.items) ?? [];
    const map = new Map<string, (typeof serviceProviders)[number]>();
    for (const sp of serviceProviders) map.set(sp.id, sp);
    return map;
  }, [spQuery.data?.pages]);

  const invoicesMap = useMemo(() => {
    const map = new Map<string, (typeof invoices)[number]>();
    for (const inv of invoices) map.set(inv.id, inv);
    return map;
  }, [invoices]);

  const refreshTransactions = useCallback(async () => {
    if (!canReadTransactions) return;
    await getAllTransactions({ from: dateRange.from, to: dateRange.to });
  }, [canReadTransactions, getAllTransactions, dateRange.from, dateRange.to]);

  const rows = useMemo(
    () =>
      transformExpensesToRows(
        transactions,
        serviceProvidersMap,
        invoicesMap,
        transactionCategories,
      ),
    [transactions, serviceProvidersMap, invoicesMap, transactionCategories],
  );

  return { rows, categories: transactionCategories, loading, error, refreshTransactions };
}
